import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { 
  Notifications as NotificationsIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

import notificationService from '../services/notificationService';
import PageHeader from '../../../components/common/PageHeader';
import SearchHeader from '../../../components/common/SearchHeader';
import ResponsiveTable from '../../../components/common/ResponsiveTable';
import PaginationComponent from '../../../components/common/PaginationComponent';
import ClientSelect from '../../../components/common/ClientSelect';

const NotificationListingPage = () => {
  const navigate = useNavigate();
  
  // Data states
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [readFilter, setReadFilter] = useState('all'); // 'all', 'read', 'unread'
  const [clientFilter, setClientFilter] = useState('all');
  
  // Sorting states
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalNotifications, setTotalNotifications] = useState(0);

  // Modal states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageDialogNotification, setMessageDialogNotification] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  // Load notifications
  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage
      };
      
      // Filter by read status
      if (readFilter === 'read') {
        params.is_read = true;
      } else if (readFilter === 'unread') {
        params.is_read = false;
      }
      
      // Filter by client
      if (clientFilter !== 'all') {
        params.client_id = clientFilter;
      }
      
      // Sorting
      params.sort_by = sortField;
      params.sort_order = sortDirection;
      
      const result = await notificationService.getAll(params);
      
      if (result.success) {
        const notificationsData = Array.isArray(result.data) ? result.data : [];
        setNotifications(notificationsData);
        setTotalNotifications(result.total !== undefined ? result.total : notificationsData.length);
        setUnreadCount(result.unread_count || 0);
      } else {
        setError(result.error);
        setNotifications([]);
        setTotalNotifications(0);
      }
    } catch (err) {
      setError('Σφάλμα φόρτωσης ειδοποιήσεων');
      setNotifications([]);
      setTotalNotifications(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, readFilter, clientFilter, sortField, sortDirection]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Handle mark as read
  const handleMarkAsRead = async (notificationId) => {
    const result = await notificationService.markAsRead(notificationId);
    if (result.success) {
      await loadNotifications();
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    const result = await notificationService.markAllAsRead();
    if (result.success) {
      await loadNotifications();
    }
  };

  // Handle delete
  const handleDelete = async (notificationId) => {
    const result = await notificationService.delete(notificationId);
    if (result.success) {
      await loadNotifications();
    }
  };

  // Confirm delete
  const confirmDelete = () => {
    if (selectedNotification) {
      handleDelete(selectedNotification.id);
      setDeleteDialogOpen(false);
      setSelectedNotification(null);
    }
  };

  // Handle selection
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(notifications.map(n => n.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (notificationId) => {
    setSelectedIds(prev => 
      prev.includes(notificationId) 
        ? prev.filter(id => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const handleViewMessage = (notification) => {
    setMessageDialogNotification(notification);
    setMessageDialogOpen(true);
  };

  const handleCloseMessageDialog = () => {
    setMessageDialogOpen(false);
    setMessageDialogNotification(null);
  };

  // Mark selected as read
  const handleMarkSelectedAsRead = async () => {
    for (const id of selectedIds) {
      await notificationService.markAsRead(id);
    }
    setSelectedIds([]);
    await loadNotifications();
  };

  // Handle sort
  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  // Filter notifications by search term (client-side for now)
  const filteredNotifications = useMemo(() => {
    return notifications.filter(notification => {
      if (!searchTerm.trim()) return true;
      const search = searchTerm.toLowerCase();
      return (
        notification.title?.toLowerCase().includes(search) ||
        notification.message?.toLowerCase().includes(search)
      );
    });
  }, [notifications, searchTerm]);

  // Table columns
  const columns = [
    {
      id: 'select',
      label: '',
      minWidth: 50,
      render: (notification) => (
        <Checkbox
          checked={selectedIds.includes(notification.id)}
          onChange={() => handleSelectOne(notification.id)}
        />
      ),
    },
    {
      id: 'title',
      label: 'Τίτλος',
      minWidth: 200,
      render: (notification) => (
        <Box>
          <Typography
            variant="body2"
            fontWeight={notification.is_read ? 'normal' : 'bold'}
            sx={{ mb: 0.5 }}
          >
            {notification.title}
          </Typography>
          {notification.message && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                {notification.message.length > 120
                  ? `${notification.message.substring(0, 120)}...`
                  : notification.message}
              </Typography>
              {notification.message.length > 120 && (
                <Button
                  size="small"
                  variant="text"
                  sx={{ alignSelf: 'flex-start', p: 0 }}
                  onClick={() => handleViewMessage(notification)}
                >
                  Προβολή πλήρους μηνύματος
                </Button>
              )}
            </Box>
          )}
        </Box>
      ),
    },
    {
      id: 'client',
      label: 'Πελάτης',
      minWidth: 150,
      sortable: true,
      sortField: 'client_name',
      render: (notification) => (
        notification.client_name ? (
          <Typography 
            variant="body2"
            sx={{ 
              cursor: 'pointer',
              '&:hover': { color: 'primary.main', textDecoration: 'underline' }
            }}
            onClick={() => navigate(`/clients/${notification.client_id}`)}
          >
            {notification.client_name}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">-</Typography>
        )
      ),
    },
    {
      id: 'type',
      label: 'Τύπος',
      minWidth: 150,
      sortable: true,
      sortField: 'type',
      render: (notification) => {
        const typeLabels = {
          'service_expiring': 'Υπηρεσία λήγει',
          'service_expired': 'Υπηρεσία έληξε',
          'service_renewed': 'Υπηρεσία ανανεώθηκε',
          'invoice_overdue': 'Τιμολόγιο εκπρόθεσμο',
          'payment_received': 'Πληρωμή λήφθηκε',
          'other': 'Άλλο'
        };
        const typeColors = {
          'service_expiring': 'warning',
          'service_expired': 'error',
          'service_renewed': 'success',
          'invoice_overdue': 'error',
          'payment_received': 'success',
          'other': 'default'
        };
        return (
          <Chip
            label={typeLabels[notification.type] || notification.type}
            color={typeColors[notification.type] || 'default'}
            size="small"
          />
        );
      },
    },
    {
      id: 'created_at',
      label: 'Ημερομηνία',
      minWidth: 150,
      sortable: true,
      sortField: 'created_at',
      render: (notification) => (
        <Typography variant="body2">
          {format(new Date(notification.created_at), 'dd/MM/yyyy HH:mm', { locale: el })}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: 'Κατάσταση',
      minWidth: 100,
      render: (notification) => (
        <Chip
          label={notification.is_read ? 'Διαβασμένο' : 'Αδιάβαστο'}
          color={notification.is_read ? 'default' : 'primary'}
          size="small"
        />
      ),
    },
    {
      id: 'actions',
      label: 'Ενέργειες',
      minWidth: 150,
      align: 'right',
      render: (notification) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          {!notification.is_read && (
            <Tooltip title="Σήμανση ως διαβασμένο">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleMarkAsRead(notification.id)}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Διαγραφή">
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setSelectedNotification(notification);
                setDeleteDialogOpen(true);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Ειδοποιήσεις"
        icon={<NotificationsIcon />}
        actions={[
          <Button
            key="mark-all-read"
            variant="outlined"
            startIcon={<MarkEmailReadIcon />}
            onClick={handleMarkAllAsRead}
            disabled={unreadCount === 0}
            sx={{ mr: 1 }}
          >
            Σήμανση όλων ως διαβασμένα
          </Button>,
          <Button
            key="mark-selected-read"
            variant="outlined"
            startIcon={<CheckCircleIcon />}
            onClick={handleMarkSelectedAsRead}
            disabled={selectedIds.length === 0}
            sx={{ mr: 1 }}
          >
            Σήμανση επιλεγμένων ({selectedIds.length})
          </Button>,
          <Button
            key="refresh"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadNotifications}
          >
            Ανανέωση
          </Button>
        ]}
      />

      <SearchHeader
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Αναζήτηση ειδοποιήσεων..."
      />

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box sx={{ minWidth: 250 }}>
          <ClientSelect
            value={clientFilter !== 'all' ? clientFilter : ''}
            onChange={(selectedId) => {
              setClientFilter(selectedId || 'all');
              setCurrentPage(1);
            }}
            label="Πελάτης"
            placeholder="Αναζήτηση πελάτη..."
            size="small"
          />
        </Box>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Κατάσταση</InputLabel>
          <Select
            value={readFilter}
            label="Κατάσταση"
            onChange={(e) => {
              setReadFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <MenuItem value="all">Όλες</MenuItem>
            <MenuItem value="unread">Αδιάβαστες</MenuItem>
            <MenuItem value="read">Διαβασμένες</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Σύνολο: {totalNotifications} ειδοποιήσεις
          {unreadCount > 0 && (
            <Chip 
              label={`${unreadCount} αδιάβαστες`} 
              color="primary" 
              size="small" 
              sx={{ ml: 1 }} 
            />
          )}
        </Typography>
      </Box>

      <ResponsiveTable
        columns={columns}
        data={filteredNotifications}
        loading={loading}
        error={error}
        emptyMessage="Δεν υπάρχουν ειδοποιήσεις"
        onSelectAll={handleSelectAll}
        selectedCount={selectedIds.length}
        totalCount={filteredNotifications.length}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        serverSide={true}
      />

      <PaginationComponent
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalItems={totalNotifications}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(value) => {
          setItemsPerPage(value);
          setCurrentPage(1);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedNotification(null);
        }}
      >
        <DialogTitle>Επιβεβαίωση διαγραφής</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτή την ειδοποίηση;
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setSelectedNotification(null);
          }}>
            Ακύρωση
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Διαγραφή
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={messageDialogOpen}
        onClose={handleCloseMessageDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{messageDialogNotification?.title || 'Πλήρες μήνυμα'}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {messageDialogNotification
              ? format(new Date(messageDialogNotification.created_at), 'dd/MM/yyyy HH:mm', { locale: el })
              : ''}
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {messageDialogNotification?.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMessageDialog}>Κλείσιμο</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationListingPage;

